import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import styles from "../css/CreateProfile.module.css";
import localStyles from "../css/MedicalAidStep.module.css";
import inputLabelStyles from "../../../../css/components/formInput.module.css";
import Button from "../../../../components/Button";
import ReadOnlyInput from "../../../../components/ReadOnlyInput";
import FormInput from "../../../../components/FormInput";
import Dropdown from "../../../../components/Dropdown";
import { listMedicalSchemeOptions } from "../../../../api/profile/create_profile";
import EligibilityButton from "../../../../components/EligibilityButton";

export default function MedicalAidStep({
  isViewMode,
  viewMedicalAid,
  setViewMedicalAid,
  viewPersonal = {},
  beneficiariesCount,
  setBeneficiariesCount,
  beneficiaries = [],
  setBeneficiaries,
  setModalStep,
  doctorOptions,
  doctorIdMap,
  setShowAddPatient,
  doctors = [],
  refreshDoctors,
  onNavigateAddDoctor,
  canAddPractitioner = true,
  onValidationError,
}) {
  const [schemeOptions, setSchemeOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listMedicalSchemeOptions();
        if (!cancelled) setSchemeOptions(Array.isArray(rows) ? rows : []);
      } catch (_) {
        if (!cancelled) setSchemeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const schemeSelectOptions = useMemo(
    () => schemeOptions.map((o) => ({ value: o.id, label: o.schemename, format: o.membernumberformat || "" })),
    [schemeOptions]
  );

  const selectedSchemeOption = useMemo(() => {
    const id = viewMedicalAid.optioncodeid;
    if (id != null && id !== "") {
      const match = schemeSelectOptions.find((o) => String(o.value) === String(id));
      if (match) return match;
    }
    // Fall back to matching the saved provider name if no id is stored yet
    if (viewMedicalAid.provider) {
      const byName = schemeSelectOptions.find((o) => o.label === viewMedicalAid.provider);
      if (byName) return byName;
    }
    return null;
  }, [viewMedicalAid.optioncodeid, viewMedicalAid.provider, schemeSelectOptions]);

  const selectedSchemeFormat = selectedSchemeOption?.format || "";

  const selectedPractitionerLabel = (() => {
    const pid = viewMedicalAid.practitionerid;
    if (!pid) return "";
    const entry = Object.entries(doctorIdMap || {}).find(([, id]) => id === pid);
    return entry ? entry[0] : "";
  })();
  const selectedPracticeNumber = useMemo(() => {
    const pid = viewMedicalAid.practitionerid;
    if (!pid) return "";
    const found = (doctors || []).find((d) => d.id === pid);
    return found?.practicenumber || "";
  }, [viewMedicalAid.practitionerid, doctors]);
  return (
    <>
      <div className={styles.modalHeader}>
        <h2>Main Member: Medical Aid Details</h2>
        <button
          className={styles.closeX}
          aria-label="Close"
          onClick={() => setShowAddPatient && setShowAddPatient(false)}
        >
          ×
        </button>
      </div>
      {isViewMode && viewMedicalAid?.patientid ? (
        <div className={localStyles.eligibilityWrap}>
          <EligibilityButton patientId={viewMedicalAid.patientid} />
        </div>
      ) : !isViewMode ? (
        <div className={localStyles.eligibilityWrap}>
          <EligibilityButton
            fields={{
              medicalAidNumber: viewMedicalAid.medicalAidNumber || "",
              optioncodeid: viewMedicalAid.optioncodeid || "",
              surname: viewPersonal.surname || "",
              firstNames: viewPersonal.firstNames || "",
              dob: viewPersonal.dob || "",
              dependentCode: viewMedicalAid.dependentCode || "00",
              sex: viewPersonal.sex || "",
              idNumber: viewPersonal.idNumber || "",
            }}
          />
        </div>
      ) : null}
      <div className={styles.stepTwoGrid}>
        <div className={styles.twoColRow}>
          {isViewMode ? (
            <ReadOnlyInput label="Medical Aid Provider" value={viewMedicalAid.provider || ""} />
          ) : (
            <div className={inputLabelStyles.formGroup}>
              <label className={inputLabelStyles.formLabel}>Medical Aid Provider</label>
              <Select
                inputId="medicalaidprovider"
                name="medicalaidprovider"
                classNamePrefix="schemeSelect"
                options={schemeSelectOptions}
                value={selectedSchemeOption}
                onChange={(opt) =>
                  setViewMedicalAid({
                    ...viewMedicalAid,
                    provider: opt ? opt.label : "",
                    optioncodeid: opt ? opt.value : "",
                  })
                }
                placeholder="Search medical aid provider"
                isClearable
                isSearchable
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          )}
          {isViewMode ? (
            <ReadOnlyInput label="Medical Aid Number" value={viewMedicalAid.medicalAidNumber || ""} />
          ) : (
            <div className={inputLabelStyles.formGroup}>
              <FormInput
                label="Medical Aid Number"
                type="text"
                name="medicalaidnumber"
                value={viewMedicalAid.medicalAidNumber || ""}
                onChange={(e) => {
                  // Allow alphanumerics and "/" so alpha/slash scheme formats
                  // (e.g. "3 Alpha & 9 Numerics", "Starts with 479/") can be entered.
                  const v = (e.target.value || "").replace(/[^A-Za-z0-9/]/g, "").toUpperCase();
                  setViewMedicalAid({ ...viewMedicalAid, medicalAidNumber: v });
                }}
              />
              {selectedSchemeFormat ? (
                <small className={localStyles.fieldHint}>
                  Expected format: {selectedSchemeFormat}
                </small>
              ) : null}
            </div>
          )}
        </div>
        <div className={styles.twoColRow}>
          {isViewMode ? (
            <>
              <ReadOnlyInput label="Practitioner" value={selectedPractitionerLabel} />
              <ReadOnlyInput label="Practice Number" value={selectedPracticeNumber} />
            </>
          ) : (
            <>
              <Dropdown
                label="Practitioner"
                name="practitionerid"
                options={[...(doctorOptions || []), "Add Doctor"]}
                value={selectedPractitionerLabel}
                onChange={(label) => {
                  if (label === "Add Doctor") {
                    if (typeof onNavigateAddDoctor === 'function') onNavigateAddDoctor();
                    return;
                  }
                  const pid = (doctorIdMap || {})[label] || "";
                  setViewMedicalAid({ ...viewMedicalAid, practitionerid: pid });
                }}
                placeholder="Select"
              />
              <div className={localStyles.practitionerRow}>
                <ReadOnlyInput label="Practice Number" value={selectedPracticeNumber} />
              </div>
            </>
          )}
        </div>

        <div className={isViewMode ? `${styles.fullWidth} ${styles.halfWidth}` : styles.fullWidth}>
          {isViewMode ? (
            <ReadOnlyInput label="Dependent Code" value={viewMedicalAid.dependentCode} />
          ) : (
            <FormInput
              label="Dependent Code"
              type="text"
              name="dependentCode"
              value={viewMedicalAid.dependentCode || ""}
              onChange={(e) => {
                const v = (e.target.value || "").replace(/[^0-9]/g, "");
                setViewMedicalAid({ ...viewMedicalAid, dependentCode: v });
              }}
            />
          )}
        </div>

        <div className={styles.fullWidth}>
          {isViewMode ? (
            <ReadOnlyInput label="Note" value={viewMedicalAid.note} />
          ) : (
            <div className={styles.textareaGroup}>
              <label className={inputLabelStyles.formLabel}>Note</label>
              <textarea
                className={styles.textarea}
                rows="3"
                value={viewMedicalAid.note || ""}
                onChange={(e) => setViewMedicalAid({ ...viewMedicalAid, note: e.target.value })}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.modalFooter}>
        <Button
          variant="secondary"
          onClick={() => {
            if (typeof setModalStep === "function") setModalStep(1);
          }}
        >
          Back
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (isViewMode) {
              setModalStep(3);
              return;
            }
            const missing = [];
            if (!viewMedicalAid.practitionerid) missing.push("Practitioner");
            if (missing.length) {
              const msg = `Please fill in the following required fields: ${missing.join(", ")}.`;
              if (typeof onValidationError === "function") {
                onValidationError("Medical aid validation", msg);
              } else {
                alert(msg);
              }
              return;
            }
            const count = Math.max(0, beneficiariesCount || 0);
            if (!Array.isArray(beneficiaries) || beneficiaries.length === 0) {
              setBeneficiaries(Array.from({ length: count }, () => ({ })));
            }
            setModalStep(3);
          }}
          className={styles.nextBtn}
        >
          Next
        </Button>
      </div>
    </>
  );
}